import { useCallback, useLayoutEffect, useRef, type RefCallback } from "react";

import { playFlip, type FlipSnapshot, type MotionDuration, type MotionEasing } from "@/lib/motion";

export type UseListTransitionOptions = {
  /** Defaults to `snappy` — optimistic and keyboard reflows must settle quickly. */
  duration?: MotionDuration;
  easing?: MotionEasing;
  /** Above this many moved rows the reflow is left un-animated. */
  maxElements?: number;
  /**
   * Set `false` to keep measuring but stop animating — for a list that is
   * mid-drag, where the pointer already owns the row positions and a FLIP
   * would fight it.
   */
  enabled?: boolean;
  /**
   * Changing this records the post-change layout without animating it.
   * Filters use this so the next real mutation starts from filtered geometry.
   */
  snapshotKey?: unknown;
};

/**
 * Animate a list *reflowing*: rows sliding to their new places when something
 * above them is inserted, removed, reordered or filtered out.
 *
 * ```tsx
 * const list = useListTransition(tasks.map((task) => task.id).join("\0"));
 * …
 * <ul ref={list}>
 *   {tasks.map((task) => <li key={task.id} {...flipProps(task.id)}>…</li>)}
 * </ul>
 * ```
 *
 * Attach the ref to the list container that already exists; mark each row with
 * `flipProps`. No wrapper element is added and no row is cloned — the hook only
 * ever reads `getBoundingClientRect` and starts a transform animation, so a row
 * that is being animated is still the same DOM node with the same class list,
 * the same event handlers and the same scroll position.
 *
 * ---------------------------------------------------------------------------
 * HOW IT GETS THE "BEFORE" POSITIONS WITHOUT A RENDER-PHASE HACK
 * ---------------------------------------------------------------------------
 *
 * A FLIP needs the layout from *before* the DOM changed, and by the time any
 * effect runs the DOM has already changed. The way out is that a layout effect
 * runs after React has mutated the DOM but **before the browser paints**, so
 * the measurement taken at the end of commit N is still true at the start of
 * commit N+1 — it is that commit's "before". The hook therefore keeps one
 * snapshot in a ref, compares against it, and replaces it every time.
 *
 * That is also why this is `useLayoutEffect` and not `useEffect`: with
 * `useEffect` the browser paints the rows in their new positions first, so the
 * user sees the jump and *then* sees it animate back and forth.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT DOES NOT DO
 * ---------------------------------------------------------------------------
 *
 * - **Rows that are new do not animate here.** They have no previous position
 *   to travel from; they fade in with `enterProps("row")`, which is the right
 *   channel for "this is new" — position is for "this moved".
 * - **Rows that are leaving do not animate at all.** An exit needs the element
 *   held in the DOM past its own removal, which means owning the list's mount,
 *   which this hook deliberately does not.
 * - **Under reduced motion it measures and animates nothing.** A reflow is
 *   movement with no other content to it, so unlike an enter there is no fade
 *   to fall back to.
 * - **A mass reflow is skipped**, not throttled — see `FLIP_MAX_ELEMENTS`.
 *
 * Returns a **callback ref**, and that is deliberate rather than incidental: a
 * `RefObject<HTMLElement>` is invariant in React's typings, so it cannot be
 * handed to `<ul ref={…}>` without the call site naming `HTMLUListElement`
 * explicitly. A callback ref is assignable to every element type, so this works
 * on a `ul`, a `div`, an `ol` or a `tbody` with nothing to declare.
 */
export function useListTransition(
  /**
   * What identifies this arrangement of the list. Change it and the hook
   * animates; typically the row keys joined, so a reorder counts as a change
   * and a re-render with the same rows does not.
   */
  arrangement: unknown,
  options: UseListTransitionOptions = {},
): RefCallback<HTMLElement> {
  const root = useRef<HTMLElement | null>(null);
  const snapshot = useRef<FlipSnapshot | null>(null);

  // Destructured rather than passed through as an object so the dependency
  // array holds primitives: an inline `{}` at the call site is a new object
  // every render and would re-run this on every render instead of on a change.
  const { duration, easing, maxElements, enabled = true, snapshotKey } = options;
  const previousSnapshotKey = useRef(snapshotKey);

  const setRoot = useCallback((element: HTMLElement | null) => {
    root.current = element;
    if (element === null) {
      // The list went away; the snapshot describes a DOM that no longer exists,
      // and keeping it would FLIP the next list mounted here from wherever the
      // old one's rows happened to be.
      snapshot.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    const element = root.current;
    if (!element) {
      return;
    }
    // `playFlip` returns the true post-layout positions — the rows are
    // transformed by the time it returns, so measuring again here would
    // capture where they are pretending to be.
    const snapshotKeyChanged = !Object.is(previousSnapshotKey.current, snapshotKey);
    previousSnapshotKey.current = snapshotKey;
    snapshot.current = playFlip(element, enabled && !snapshotKeyChanged ? snapshot.current : null, {
      duration,
      easing,
      maxElements,
    });
  }, [arrangement, duration, easing, maxElements, enabled, snapshotKey]);

  return setRoot;
}
